import { useState } from "react";
import {
  useRunAssetResearchMutation,
  useGetRecentAssetResearchQuery,
  useGetAssetResearchRunQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const rugTone: Record<string, string> = {
  MINIMAL: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  LOW: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  MEDIUM: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  HIGH: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  CRITICAL: "bg-rose-600/25 text-rose-200 border-rose-600/40",
};

const verdictTone: Record<string, string> = {
  GO: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  WAIT: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  SKIP: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  AVOID: "bg-rose-600/25 text-rose-200 border-rose-600/40",
};

export default function Research() {
  const [q, setQ] = useState("");
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [run, runState] = useRunAssetResearchMutation();
  const recent = useGetRecentAssetResearchQuery(30, { pollingInterval: 60000 });
  const selected = useGetAssetResearchRunQuery(selectedRunId ?? 0, {
    skip: selectedRunId == null,
  });

  // Use the selected past run if one's been clicked, otherwise the
  // most-recent run mutation result. selected.data.payload mirrors the
  // shape returned by /run.
  const result: any = selectedRunId != null
    ? (selected.data?.payload ?? null)
    : runState.data;
  const g: any = result?.gemini || {};
  const v: any = g.verdict || {};
  const rug: any = g.rug_pull_assessment || {};
  const strat: any = g.strategy_fit || {};
  const pred: any = g.prediction || {};
  const events: any[] = g.upcoming_events || [];
  const sources: any[] = g.sources || [];
  const tech: any = result?.technicals || {};
  const tok: any = result?.tokenomics || null;
  const tvl: any = result?.tvl || null;
  const cat: any = result?.catalysts || null;
  const onchain: any = result?.onchain || null;
  const rugLocal: any = result?.rug_pull_local || null;

  async function go() {
    if (!q.trim()) return;
    setSelectedRunId(null);
    await run({ query: q.trim() }).unwrap();
    recent.refetch();
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Asset Deep Research</h1>
        <p className="text-sm text-muted max-w-2xl">
          Type any symbol (e.g. <span className="font-mono">SOL</span> or
          <span className="font-mono"> SOL/USDT</span>) or a BSC contract
          (<span className="font-mono">0x…</span>). Pulls technicals + tokenomics +
          TVL + catalysts + on-chain data + a Gemini Pro synthesis with Google
          Search (rug-pull check, upcoming events, which of our engines fits).
          No new paid APIs.
        </p>
      </header>

      <Card>
        <CardContent className="p-3">
          <div className="flex gap-2 flex-wrap">
            <input type="text"
                   placeholder="SOL, SOL/USDT, 0xb8c77482e..."
                   className="flex-1 min-w-[280px] bg-bg text-fg border border-border rounded px-3 py-2 font-mono"
                   value={q}
                   onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") go(); }} />
            <Button onClick={go} disabled={runState.isLoading || !q.trim()}>
              {runState.isLoading ? "Researching…" : "Run deep research"}
            </Button>
          </div>
          {runState.error && (
            <div className="text-xs text-rose-400 mt-1">
              {(runState.error as any)?.data?.detail || "request failed"}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedRunId != null && (
        <div className="flex items-center gap-2 text-xs">
          <Badge tone="info">Viewing past run #{selectedRunId}</Badge>
          <button className="text-muted hover:text-fg underline"
                  onClick={() => setSelectedRunId(null)}>
            clear selection
          </button>
        </div>
      )}

      {result && (
        <>
          {/* TOP SUMMARY */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
                <span>
                  {result.symbol} <span className="text-muted text-sm font-normal">· {result.asset_kind}</span>
                </span>
                <div className="flex gap-1 flex-wrap">
                  {v.decision && (
                    <Badge className={verdictTone[v.decision] || ""}>
                      {v.decision} · {v.confidence ?? "?"}%
                    </Badge>
                  )}
                  {rug.risk_level && (
                    <Badge className={rugTone[rug.risk_level] || ""}>
                      rug: {rug.risk_level}
                    </Badge>
                  )}
                  {strat.best_engine && (
                    <Badge className="bg-violet-500/15 text-violet-300 border-violet-500/30">
                      fit: {strat.best_engine}
                    </Badge>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              {g.narrative_summary && (
                <p className="text-fg/90">{g.narrative_summary}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {v.key_thesis && (
                  <div className="border border-emerald-500/30 bg-emerald-500/5 rounded p-2">
                    <div className="text-xs text-emerald-300 uppercase mb-1">Thesis</div>
                    <div className="text-sm">{v.key_thesis}</div>
                  </div>
                )}
                {v.key_risk && (
                  <div className="border border-rose-500/30 bg-rose-500/5 rounded p-2">
                    <div className="text-xs text-rose-300 uppercase mb-1">Risk</div>
                    <div className="text-sm">{v.key_risk}</div>
                  </div>
                )}
              </div>
              {v.size_recommendation_usd != null && (
                <div className="text-xs text-muted">
                  Suggested size: <span className="text-fg">${Number(v.size_recommendation_usd).toFixed(2)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* STRATEGY FIT */}
          {strat.engines_ranked?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Strategy fit</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {strat.engines_ranked.map((e: any, i: number) => (
                  <div key={i} className="border border-border bg-bg2 rounded p-2 flex items-start gap-3">
                    <div className="text-2xl font-bold tabular w-12 shrink-0 text-center">
                      {e.fit_score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm font-semibold">{e.engine}</div>
                      <div className="text-xs text-muted">{e.reasoning}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* PREDICTION */}
          {pred["7d_bias"] && (
            <Card>
              <CardHeader><CardTitle className="text-base">Price prediction</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="border border-border bg-bg2 rounded p-3">
                  <div className="text-xs text-muted">7-day bias</div>
                  <div className="text-lg font-semibold">
                    {pred["7d_bias"]} · {pred.confidence_pct ?? "?"}% confidence
                  </div>
                  <div className="text-xs text-muted mt-1">
                    Range: ${pred["7d_low"]} – ${pred["7d_high"]}
                  </div>
                </div>
                <div className="border border-border bg-bg2 rounded p-3">
                  <div className="text-xs text-muted">30-day bias</div>
                  <div className="text-lg font-semibold">{pred["30d_bias"]}</div>
                  <div className="text-xs text-muted mt-1">
                    Range: ${pred["30d_low"]} – ${pred["30d_high"]}
                  </div>
                </div>
                {pred.key_drivers?.length > 0 && (
                  <div className="md:col-span-2 text-xs text-muted">
                    <span className="text-fg/80">Drivers:</span> {pred.key_drivers.join(" · ")}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* RUG-PULL ASSESSMENT */}
          <Card>
            <CardHeader><CardTitle className="text-base">Rug-pull assessment</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <Field label="Team" value={rug.team_status || "?"} />
                <Field label="Audit" value={rug.audit_status || "?"} />
                <Field label="LP lock" value={rug.lp_lock_status || "?"} />
                <Field label="Verdict" value={rug.verdict || "?"} />
              </div>
              {rug.specific_findings?.length > 0 && (
                <ul className="text-xs space-y-1">
                  {rug.specific_findings.map((f: string, i: number) => (
                    <li key={i}>· {f}</li>
                  ))}
                </ul>
              )}
              {rugLocal?.flags?.length > 0 && (
                <div className="text-xs text-amber-300 border-t border-border pt-2 mt-2">
                  Local heuristic ({rugLocal.risk_level}, score {rugLocal.score}):
                  <ul className="mt-1 space-y-0.5">
                    {rugLocal.flags.map((f: string, i: number) => <li key={i}>· {f}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* UPCOMING EVENTS */}
          {events.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Upcoming events</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-muted">
                    <th className="py-1 pr-3">Date</th>
                    <th className="py-1 pr-3">Event</th>
                    <th className="py-1 pr-3">Category</th>
                    <th className="py-1 pr-3">Impact</th>
                    <th className="py-1 pr-3">Direction</th>
                  </tr></thead>
                  <tbody>
                    {events.map((e: any, i: number) => (
                      <tr key={i} className="border-t border-border/40">
                        <td className="py-1 pr-3 text-xs text-muted font-mono">{e.date}</td>
                        <td className="py-1 pr-3">{e.event}</td>
                        <td className="py-1 pr-3 text-xs">{e.category}</td>
                        <td className="py-1 pr-3 text-xs">{e.impact}</td>
                        <td className={`py-1 pr-3 text-xs ${e.direction === "bullish" ? "text-emerald-400" : e.direction === "bearish" ? "text-rose-400" : "text-muted"}`}>
                          {e.direction}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* TECHNICALS */}
          {tech.available_on_binance && tech.timeframes && (
            <Card>
              <CardHeader><CardTitle className="text-base">Technicals</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {Object.entries(tech.timeframes).map(([tf, t]: [string, any]) => (
                    <div key={tf} className="border border-border bg-bg2 rounded p-2 text-xs">
                      <div className="font-mono font-semibold mb-1">{tf}</div>
                      <Row k="trend" v={t.trend ?? "?"} />
                      <Row k="RSI(14)" v={t.rsi_14} />
                      <Row k="SMA50" v={t.sma_50} />
                      <Row k="SMA200" v={t.sma_200} />
                      <Row k="ATR%" v={`${t.atr_pct}%`} />
                    </div>
                  ))}
                </div>
                {tech.grid_regime && (
                  <div className="mt-2 text-xs">
                    Grid regime fit: score{" "}
                    <span className="font-mono">{tech.grid_regime.score}</span> ·{" "}
                    <span className={tech.grid_regime.verdict === "grid_friendly" ? "text-emerald-400" : tech.grid_regime.verdict === "disable" ? "text-rose-400" : "text-amber-400"}>
                      {tech.grid_regime.verdict}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* TOKENOMICS + TVL + ONCHAIN */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tok && (
              <Card>
                <CardHeader><CardTitle className="text-base">Tokenomics (CoinGecko)</CardTitle></CardHeader>
                <CardContent className="text-xs space-y-1">
                  <Row k="market cap" v={tok.market_cap_usd ? `$${(tok.market_cap_usd/1e6).toFixed(2)}M` : "?"} />
                  <Row k="FDV" v={tok.fdv_usd ? `$${(tok.fdv_usd/1e6).toFixed(2)}M` : "?"} />
                  <Row k="FDV/mcap" v={`${tok.fdv_mcap_ratio ?? "?"}x · ${tok.overhang_tag ?? "—"}`} />
                  <Row k="circulating supply"
                       v={tok.circulating_supply ? Number(tok.circulating_supply).toLocaleString() : "?"} />
                  <Row k="total supply"
                       v={tok.total_supply ? Number(tok.total_supply).toLocaleString() : "?"} />
                  <Row k="max supply"
                       v={tok.max_supply ? Number(tok.max_supply).toLocaleString() : "uncapped"} />
                  <Row k="circulating %" v={tok.circulating_pct_of_total != null ? `${tok.circulating_pct_of_total}%` : "?"} />
                  {tok.total_supply && tok.circulating_supply ? (
                    <Row k="locked/vested overhang"
                         v={`${Number(tok.total_supply - tok.circulating_supply).toLocaleString()} (${(((tok.total_supply - tok.circulating_supply) / tok.total_supply) * 100).toFixed(1)}%)`} />
                  ) : null}
                  <Row k="market cap rank" v={tok.market_cap_rank ?? "?"} />
                  <Row k="ATH zone" v={tok.ath_zone ?? "?"} />
                </CardContent>
              </Card>
            )}
            {tvl && tvl.available !== false && (
              <Card>
                <CardHeader><CardTitle className="text-base">TVL (DeFiLlama)</CardTitle></CardHeader>
                <CardContent className="text-xs space-y-1">
                  <Row k="TVL now" v={tvl.tvl_usd ? `$${(tvl.tvl_usd/1e6).toFixed(2)}M` : "?"} />
                  <Row k="trend" v={tvl.tvl_trend_tag ?? "?"} />
                  <Row k="mcap/TVL" v={tvl.mcap_to_tvl_ratio ?? "?"} />
                </CardContent>
              </Card>
            )}
            {onchain?.dexscreener && (
              <Card>
                <CardHeader><CardTitle className="text-base">On-chain (DexScreener)</CardTitle></CardHeader>
                <CardContent className="text-xs space-y-1">
                  <Row k="price" v={`$${onchain.dexscreener.price_usd}`} />
                  <Row k="liquidity" v={`$${(onchain.dexscreener.liquidity_usd || 0).toLocaleString()}`} />
                  <Row k="24h vol" v={`$${(onchain.dexscreener.volume_24h_usd || 0).toLocaleString()}`} />
                </CardContent>
              </Card>
            )}
          </div>

          {/* CATALYSTS */}
          {cat && cat.recent_catalysts?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Recent catalysts ({cat.overall_sentiment})</CardTitle></CardHeader>
              <CardContent>
                <ul className="text-sm space-y-1">
                  {cat.recent_catalysts.map((c: any, i: number) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-muted text-xs font-mono">{c.date}</span>
                      <span className={c.sentiment === "positive" ? "text-emerald-400" : c.sentiment === "negative" ? "text-rose-400" : ""}>
                        {c.headline}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* SOURCES */}
          {sources.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base text-muted">Sources ({sources.length})</CardTitle></CardHeader>
              <CardContent>
                <ul className="text-xs space-y-1">
                  {sources.map((s: any, i: number) => (
                    <li key={i}>
                      <a href={s.uri} target="_blank" rel="noreferrer" className="text-violet-300 hover:underline">
                        {s.title || s.uri}
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* RECENT RUNS */}
      <Card>
        <CardHeader><CardTitle className="text-base">Recent research runs</CardTitle></CardHeader>
        <CardContent>
          {(recent.data?.runs || []).length === 0 ? (
            <div className="text-sm text-muted">No runs yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted">
                  <th className="py-1 pr-3">When</th>
                  <th className="py-1 pr-3">Query</th>
                  <th className="py-1 pr-3">Symbol</th>
                  <th className="py-1 pr-3">Decision</th>
                  <th className="py-1 pr-3">Conf%</th>
                  <th className="py-1 pr-3">Best engine</th>
                  <th className="py-1 pr-3">Rug</th>
                </tr></thead>
                <tbody>
                  {(recent.data?.runs || []).map((r: any) => {
                    const active = selectedRunId === r.id;
                    return (
                      <tr key={r.id}
                          className={`border-t border-border/40 cursor-pointer hover:bg-bg ${active ? "bg-bg" : ""}`}
                          onClick={() => {
                            setSelectedRunId(r.id);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}>
                        <td className="py-1 pr-3 text-xs text-muted">{r.ts?.slice(0, 19).replace("T", " ")}</td>
                        <td className="py-1 pr-3 font-mono text-xs">{r.input_query}</td>
                        <td className="py-1 pr-3 font-mono text-xs">{r.symbol}</td>
                        <td className="py-1 pr-3">
                          {r.decision && <Badge className={verdictTone[r.decision] || ""}>{r.decision}</Badge>}
                        </td>
                        <td className="py-1 pr-3 text-right tabular">{r.confidence}</td>
                        <td className="py-1 pr-3 text-xs">{r.best_engine}</td>
                        <td className="py-1 pr-3">
                          {r.rug_risk_level && <Badge className={rugTone[r.rug_risk_level] || ""}>{r.rug_risk_level}</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v }: { k: string; v: any }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted">{k}</span>
      <span className="font-mono">{v ?? "?"}</span>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border bg-bg rounded p-2">
      <div className="text-muted">{label}</div>
      <div className="font-mono">{value}</div>
    </div>
  );
}
